import React, { useCallback, useRef } from 'react'
import {
    SearchResultsListingsGridQuery_searchResults_SearchResults_intercept_InterceptCTA,
    SearchResultsListingsGridQuery_searchResults_SearchResults_listings,
    SearchResultsListingsGridQuery_searchResults_SearchResults_listings_SearchListing,
    SearchResultsListingsGridQuery_searchResults_SearchResults_listings_GPTListing,
    SearchResultsListingsGridQuery_searchResults_SearchResults_listings_LeasingListing,
} from '../../contexts/search-results-context/query/__generated__/search-results-listings.fed.query'
import { ListingType } from '../../__generated__/fed-graphql-types'
import { SearchListing } from '../search-listing/search-listing'
import GPTInSearch from '../../components/gpt-insearch/gpt-insearch'
import { StyledGPTWrapper, StyledSearchLI, StyledSearchUl } from './search-ul.styles'
import { ErrorBoundary } from '@at/design-system-patterns'
import { LeasingListing } from '../search-listing/leasing-listing'
import { useAppContext } from '@at/sauron-platform-core'
import { StyledListingStandout } from '../search-listing/search-listing.styles'
import { prominenceLabelText } from '../../utils/search/search-results-util/search-results.util'
import { Intercept } from '../search-listing/intercept/intercept'
import { useForm } from '../../forms/hocs/form.context'
import { useObserveScreenWidth } from '../../hooks/use-observe-screen-width.hook'
import { showIntercept } from './intercept.util'
import { useATTrackEvent } from '@at/consumer-web-snowplow'
import { AdvertContext203 } from '@at/track-types'

export const SearchUl = ({
    searchUlContainerRef,
    listings,
    savedAdverts,
    intercept,
}: {
    searchUlContainerRef: React.MutableRefObject<HTMLUListElement | null>
    listings: ReadonlyArray<SearchResultsListingsGridQuery_searchResults_SearchResults_listings>
    savedAdverts: string[]
    page: number | undefined
    searchId: string
    intercept?: SearchResultsListingsGridQuery_searchResults_SearchResults_intercept_InterceptCTA | null
}) => {
    const { ssrMode } = useAppContext()
    const { totalResults, selectedMap, sortBySelected } = useForm()

    const positionForIntercept = [12, 60]

    const shouldShowIntercept = showIntercept(selectedMap, sortBySelected, intercept?.interceptType)
    const observerRefs = useRef(new Map<HTMLLIElement, MutationObserver>())

    const { isMobile } = useObserveScreenWidth()
    const fireAdBlockerEvent = useATTrackEvent()
    const observerCallback = useCallback(
        (elementRef: HTMLLIElement | null) => {
            if (!elementRef) {
                return
            }

            if (observerRefs.current.has(elementRef)) {
                observerRefs.current.get(elementRef)?.disconnect()
                observerRefs.current.delete(elementRef)
            }

            const observer = new MutationObserver(() => {
                if (window.getComputedStyle(elementRef).display === 'none') {
                    fireAdBlockerEvent({
                        fireEventEntity: {
                            SCHEMA_KEY: 'StateChange100',
                            label: `ad-blocker`,
                            prev_state: 'visible',
                            curr_state: 'hidden',
                        },
                        customiseEntities: (entities) => {
                            return [
                                ...entities,
                                {
                                    SCHEMA_KEY: 'AdvertContext203',
                                    id: elementRef.dataset.advertid,
                                } as AdvertContext203,
                            ]
                        },
                    })
                }
            })
            observer.observe(elementRef, { attributes: true, attributeFilter: ['style'] })
            observerRefs.current.set(elementRef, observer)
        },
        [fireAdBlockerEvent]
    )

    if (totalResults === 0) return null

    return (
        <StyledSearchUl ref={searchUlContainerRef}>
            {listings?.map((listing, i: number) => {
                const prominenceLabel = prominenceLabelText(listing?.type)
                if (
                    shouldShowIntercept &&
                    positionForIntercept.includes(i) &&
                    isMobile &&
                    intercept &&
                    intercept?.interceptType
                ) {
                    return (
                        <Intercept
                            key={i}
                            position={i}
                            interceptType={intercept.interceptType}
                            title={intercept.title}
                            subtitle={intercept.subtitle}
                            buttonText={intercept.buttonText}
                            ctaUrl={intercept.ctaUrl}
                            sort={intercept.sort}
                        />
                    )
                }

                switch (listing.type) {
                    case ListingType.NATURAL_LISTING:
                    case ListingType.PROMOTED_LISTING:
                    case ListingType.FEATURED_LISTING:
                    case ListingType.YOU_MAY_ALSO_LIKE:
                    case ListingType.DEALER_STOCK_LISTING:
                        const searchListing =
                            listing as SearchResultsListingsGridQuery_searchResults_SearchResults_listings_SearchListing
                        return (
                            <StyledSearchLI
                                key={`${searchListing.advertId}${i}`}
                                $hasProminenceLabel={prominenceLabel}
                                ref={observerCallback}
                                id={`id-${searchListing.advertId}`}
                                data-testid={`id-${searchListing.advertId}`}
                                data-advertid={searchListing.advertId}
                            >
                                <SearchListing
                                    listing={searchListing}
                                    index={i}
                                    saveAdvertIsSaved={!!savedAdverts.find((ad) => ad === searchListing.advertId)}
                                />
                            </StyledSearchLI>
                        )
                    case ListingType.GPT_LISTING:
                        const gptListing =
                            listing as SearchResultsListingsGridQuery_searchResults_SearchResults_listings_GPTListing
                        if (ssrMode) {
                            return null
                        }
                        return (
                            <StyledSearchLI key={`${gptListing.posId}${i}`} $hasProminenceLabel={true}>
                                <StyledListingStandout as="span" data-testid={'GPT_LISTING'}>
                                    Ad
                                </StyledListingStandout>
                                <ErrorBoundary fallback={<></>} errorLevel="fallback">
                                    <StyledGPTWrapper>
                                        <GPTInSearch listing={gptListing} />
                                    </StyledGPTWrapper>
                                </ErrorBoundary>
                            </StyledSearchLI>
                        )
                    case ListingType.LEASING_LISTING:
                        const leasingListing =
                            listing as SearchResultsListingsGridQuery_searchResults_SearchResults_listings_LeasingListing
                        return (
                            <StyledSearchLI key={`${leasingListing.advertId}${i}`} $hasProminenceLabel={true}>
                                <LeasingListing listing={leasingListing} index={i} />
                            </StyledSearchLI>
                        )
                }
            })}
        </StyledSearchUl>
    )
}
